import { Button, Empty, Space, Tag, Tooltip, Typography } from 'antd'
import { StyledTable } from 'components/TableStyles/TableStyles'
import moment from 'moment'
import PropTypes from 'prop-types'
import React from 'react'
import AuditChanges from '../AuditChanges'
import { DATE_TIME_FORMAT, DESTRUCTIVE_ACTIONS, labelForAction } from '../../constants'

const { Text } = Typography

/**
 * Formats a stored UTC timestamp in IST.
 *
 * The column header says IST for the same reason: these rows are read during
 * incident reviews, and a timestamp whose zone is ambiguous is worse than no
 * timestamp.
 * @param {string} value ISO timestamp.
 * @returns {string} Display string.
 */
const formatOccurredAt = (value) =>
  value ? moment(value).utcOffset('+05:30').format(DATE_TIME_FORMAT) : '—'

const buildColumns = () => [
  {
    title: 'When (IST)',
    dataIndex: 'occurredAt',
    key: 'occurredAt',
    width: 190,
    render: formatOccurredAt,
  },
  {
    title: 'Who',
    dataIndex: 'actorEmail',
    key: 'actor',
    width: 220,
    render: (actorEmail, record) => (
      <Space direction="vertical" size={0}>
        <Text>{actorEmail || record.actorId || 'System'}</Text>
        {record.actorRole ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.actorRole}
          </Text>
        ) : null}
      </Space>
    ),
  },
  {
    title: 'Action',
    dataIndex: 'action',
    key: 'action',
    width: 220,
    render: (action) => (
      <Tag color={DESTRUCTIVE_ACTIONS.has(action) ? 'volcano' : 'blue'}>
        {labelForAction(action)}
      </Tag>
    ),
  },
  {
    title: 'Entity',
    dataIndex: 'entityLabel',
    key: 'entity',
    render: (entityLabel, record) => (
      <Space direction="vertical" size={0}>
        <Text>{entityLabel || record.entityId || '—'}</Text>
        {record.entityType ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.entityType.replace(/_/g, ' ')}
          </Text>
        ) : null}
      </Space>
    ),
  },
  {
    title: 'Source',
    dataIndex: 'ip',
    key: 'ip',
    width: 150,
    render: (ip, record) => (
      <Tooltip title={record.service}>
        <Text type="secondary">{ip || '—'}</Text>
      </Tooltip>
    ),
  },
]

const hasDetail = (record) =>
  Boolean(
    (record.changes && Object.keys(record.changes).length) ||
      (record.metadata && Object.keys(record.metadata).length)
  )

/**
 * The audit trail table.
 *
 * Paging is a "Load more" button rather than a numbered pager: the API is
 * keyset-paginated (an append-only table makes OFFSET degrade), so there is no
 * total count to page against.
 */
const AuditTable = ({
  rows,
  isLoading,
  isLoadingMore,
  hasMore,
  isFiltered,
  onLoadMore,
}) => (
  <>
    <StyledTable
      rowKey="id"
      columns={buildColumns()}
      dataSource={rows}
      loading={isLoading}
      pagination={false}
      scroll={{ x: 1100 }}
      expandable={{
        expandedRowRender: (record) => <AuditChanges record={record} />,
        rowExpandable: hasDetail,
      }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              isFiltered
                ? 'No activity matches these filters. Try widening the date range.'
                : 'No activity recorded yet. Assignments, edits, deletions and permission changes will appear here.'
            }
          />
        ),
      }}
    />

    {hasMore ? (
      <div style={{ textAlign: 'center', padding: '16px 0' }}>
        <Button onClick={onLoadMore} loading={isLoadingMore}>
          Load more
        </Button>
      </div>
    ) : null}
  </>
)

AuditTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  hasMore: PropTypes.bool,
  isFiltered: PropTypes.bool,
  onLoadMore: PropTypes.func.isRequired,
}

AuditTable.defaultProps = {
  rows: [],
  isLoading: false,
  isLoadingMore: false,
  hasMore: false,
  isFiltered: false,
}

export default AuditTable
